/**
 * DocumentsTab — User Story US-1: Downloadable documents + codebase zip.
 *
 * Composes two tasks:
 *   - PDFDownloadCard  (one per PDF in the catalog)
 *   - CodebaseDownloadCard (single, generates fresh zip on click)
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import PDFDownloadCard from './tasks/PDFDownloadCard';
import CodebaseDownloadCard from './tasks/CodebaseDownloadCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/documentation/files`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch (e) {
      console.error('Failed to fetch documentation files', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return (
    <div className="space-y-6" data-testid="documents-tab">
      {/* Section: Technical Documents */}
      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-1">📄 Technical Documents</h2>
        <p className="text-sm text-slate-500 mb-4">
          PDFs you can hand to a new developer or tech lead. Each is freshly generated from the source markdown in this repo.
        </p>

        {loading ? (
          <Card className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading documents…
          </Card>
        ) : files.length === 0 ? (
          <Card className="p-6 text-center text-slate-500" data-testid="documents-empty">
            No documents available yet. Ask the developer to run{' '}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/app/scripts/generate_docs_pdf.py</code>.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {files.map((file) => (
              <PDFDownloadCard key={file.name} file={file} />
            ))}
          </div>
        )}
      </section>

      {/* Section: Codebase */}
      <section className="pt-2">
        <h2 className="text-lg font-bold text-slate-900 mb-1">💾 Codebase</h2>
        <p className="text-sm text-slate-500 mb-4">
          Take the source code anywhere. Bundle ships with step-by-step setup +
          deploy instructions for local, VPS, Docker, and managed PaaS.
        </p>
        <CodebaseDownloadCard />
      </section>
    </div>
  );
}
